import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { DataTable, DataTableRowEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import { FilterMatchMode } from "primereact/api";
import { useSearchParams } from "react-router-dom";
import { InitDataContext } from "../../../context/InitDataContext";
import {
  GetHttpErrorMessage,
  SHOW_MSG_ACTION,
  showMsg,
  USUARIOS_FINANZAS,
} from "../../../utils/Constants.tsx";
import * as api from "../../../utils/Api.ts";
import { Toast } from "primereact/toast";
import PdfCreator from "../../../components/generate-pdf-from-data/generatePdfFromDataOrdenes";
import AccionesCumplimiento, {
  TIPO_FACTURA_EXENTA,
  netoFactura,
} from "./ConversionMoneda";

// Values accepted from the URL (same as estadoOrdenOptions / estadoPagoOptions)
const ESTADOS_ORDEN_URL = [2, 3, 4, 5];
const ESTADOS_PAGO_URL = ["PAGADA", "PENDIENTE", "ANULADA", "PROCESADA"];

export interface DetallesOrden {
  id: number;
  cantidad: number;
  idItem: number;
  itemCodigo: string;
  itemNombre: string;
  observaciones: string;
  precioTotal: number;
  precioUnitario: number;
}
// --- Types ---
export interface InvoiceItem {
  tipoDocumento: number;
  nombreDocumento: string;
  numeroFacturaLegal: number;
  numeroFacturaRea: number;
  numeroComprobante: number;
  fechaFactura?: string;
  facturaGlosa?: string;
  identificadorProveedorFactura?: string;
  nombreProveedor: string;
  numeroComprobanteReferencia: number;
  estadoPago?: string;
  estadoFactura?: string;
  montoFactura?: number;
  montoConHis?: number;
  recepcionesArray?: string;
  facturaDetalleArray?: string;
  conhisArray?: string;
  pdfUrl?: string;
}

export interface OrdenCompraGroupedResponse {
  nroOrdenCompra: number;
  montoTotalNetoOrden: number;
  estadoOrden?: number;
  tipoOrden?: number;
  identificadorProveedor?: string;
  fechaMovimientoContable?: string;
  fechaOrden: string;
  ocEstadoId?: number;
  ocImpuestoEspecifico?: number;
  ocSubdocId?: number;
  ocTasaCambio?: number;
  ocTipmonId?: number;
  ocUsuarioCreador?: number;
  proveedorId: number;
  proveedorNombre: string;
  proveedorNombreCompuesto: string;
  detallesOrden: DetallesOrden[];
  facturas: InvoiceItem[];
}

export interface Recepcion {
  AdqRcNum: number;
  AdqRcFch: string;
  AdqRcGlo1: string;
  AdqRcGlo2: string;
}

export interface FacturaDetalle {
  FcoTotNom: string;
  FcoTotVal: number;
}

export interface ConhisItem {
  tipo_movimiento: number;
  cuenta_contable: string;
  centro_costo: number;
  monto_nacional: number;
  monto_otro: number;
  glosa: string;
}

// --- SII Status Modal Component ---
const SiiDteStatusModal: React.FC<{
  visible: boolean;
  onHide: () => void;
  invoiceData: InvoiceItem | null;
  parentOrden: OrdenCompraGroupedResponse | null;
  toast: React.RefObject<Toast>;
}> = ({ visible, onHide, invoiceData, parentOrden, toast }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [siiResult, setSiiResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setSiiResult(null);
      setError(null);
      setLoading(false);
    }
  }, [visible, invoiceData]);

  const handleConsult = async () => {
    if (!invoiceData || !parentOrden) return;

    setLoading(true);
    setError(null);
    setSiiResult(null);

    try {
      const rutClean = (
        invoiceData.identificadorProveedorFactura ||
        parentOrden.identificadorProveedor ||
        ""
      )
        .replace(/\./g, "")
        .trim()
        .split("-");

      const rutEmisor = rutClean[0] || "";
      const dvEmisor = rutClean[1] || "0";

      const bodyPayload = {
        rutCompany: "96555120",
        dvCompany: "K",
        rutEmisor: rutEmisor,
        dvEmisor: dvEmisor,
        tipoDte: 33,
        folio: invoiceData.numeroFacturaLegal,
        fechaEmision: invoiceData.fechaFactura
          ? invoiceData.fechaFactura.split("T")[0]
          : "",
        montoTotal: Math.round(
          (invoiceData.montoFactura || parentOrden.montoTotalNetoOrden) * 1.19,
        ),
        pfxBase64: "",
        pfxPass: "",
      };

      const response = await api.GET_SII_INVOICE.getSiiInvoice(
        JSON.stringify(bodyPayload),
      );

      if (response !== null && response !== undefined) {
        setSiiResult(response);
      } else {
        const errorMsg = "Error capturando respuesta del servicio SII.";
        setError(errorMsg);
        showMsg(toast, "danger", "Error SII", errorMsg);
      }
    } catch (err: any) {
      console.error("Error querying SII WS:", err);
      const errMsg =
        err.response?.data?.message ||
        "Error de comunicación con el servicio web del SII";
      setError(errMsg);
      showMsg(toast, "danger", "Error de Conexión", errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      header={`Consulta SII - Factura N° ${invoiceData?.numeroFacturaLegal || ""}`}
      visible={visible}
      style={{ width: "550px" }}
      onHide={onHide}
      footer={
        <Button
          label="Cerrar"
          icon="pi pi-times"
          className="p-button-text"
          onClick={onHide}
        />
      }
    >
      {invoiceData && parentOrden && (
        <div className="flex flex-column gap-3">
          <div className="p-3 border-1 border-300 border-round bg-gray-50 text-sm">
            <p className="m-1">
              <strong>Proveedor:</strong>{" "}
              {invoiceData.facturaGlosa?.trim() || "-"}
            </p>
            <p className="m-1">
              <strong>RUT:</strong>{" "}
              {invoiceData.identificadorProveedorFactura ||
                parentOrden.identificadorProveedor ||
                "-"}
            </p>
            <p className="m-1">
              <strong>Folio:</strong> {invoiceData.numeroFacturaLegal}
            </p>
            <p className="m-1">
              <strong>Monto Total (c/IVA Est.):</strong> $
              {Math.round(
                (invoiceData.montoFactura || parentOrden.montoTotalNetoOrden) *
                  1.19,
              ).toLocaleString("es-CL")}
            </p>
          </div>

          {!siiResult && !loading && (
            <Button
              label="Consultar Estado en el SII"
              icon="pi pi-search"
              className="p-button-primary p-button-raised w-full"
              onClick={handleConsult}
              style={{
                color: "white",
                padding: "10px",
                backgroundColor: "#4a41f4",
              }}
            />
          )}

          {loading && (
            <div className="flex flex-column align-items-center justify-content-center p-4">
              <ProgressSpinner style={{ width: "40px", height: "40px" }} />
              <p className="text-sm text-600 mt-2">
                Firmando semilla y autenticando con SII...
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-100 text-red-700 border-round text-sm">
              <i className="pi pi-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          {siiResult && (
            <div className="p-3 border-1 border-300 border-round">
              <h4 className="mt-0 mb-2 font-bold text-700">
                Resultado Oficial SII:
              </h4>
              <div className="flex align-items-center gap-2 mb-3">
                <span>Estado:</span>
                <Tag
                  value={siiResult.glosaEstado || "S/I"}
                  severity={
                    siiResult.codigoEstado === "DOK" ? "success" : "danger"
                  }
                />
              </div>
              <p className="text-sm text-600 my-1">
                <strong>Código Respuesta:</strong>{" "}
                {siiResult.codigoEstado || "-"}
              </p>
              <p className="text-sm text-600 my-1">
                <strong>Observaciones SII:</strong> {siiResult.glosaSII || "-"}
              </p>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export const OrdenesConsolidadoTable: React.FC<{ contextmenu: string }> = ({
  contextmenu,
}) => {
  const [data, setData] = useState<OrdenCompraGroupedResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [expandedInvoiceRows, setExpandedInvoiceRows] = useState<any>(null);
  const [backgroundSyncing, setBackgroundSyncing] = useState<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);

  const { initData, parametros } = useContext(InitDataContext);
  const ordenesFacturasPagos = useRef<any>();
  const toast = useRef<Toast>(null);
  const ordenesCompraApprovalStates = useRef<any[]>([]);
  const ordenesSignApprovalHistory = useRef<any[]>([]);
  const dataProveedor = useRef<any>({});

  // Optional presets from the finanzas dashboard big numbers
  // (?estadoOrden=4 | ?estadoPago=PAGADA); read once, invalid values ignored
  const [searchParams] = useSearchParams();

  // --- Invoice Search Filter States ---
  const [filterNumFactura, setFilterNumFactura] = useState<string>("");
  const [filterTipoDocumento, setFilterTipoDocumento] = useState<number | null>(
    null,
  );
  const [filterEstadoFactura, setFilterEstadoFactura] = useState<string | null>(
    null,
  );
  const [filterEstadoPago, setFilterEstadoPago] = useState<string | null>(
    () => {
      const estadoPago = searchParams.get("estadoPago");
      return estadoPago && ESTADOS_PAGO_URL.includes(estadoPago)
        ? estadoPago
        : null;
    },
  );
  const [filterFechaFactura, setFilterFechaFactura] = useState<Date | null>(
    null,
  );

  // --- Purchase Order Column Filter States ---
  const initColumnFilters = () => ({
    tipoOrden: { value: null, matchMode: FilterMatchMode.EQUALS },
    nroOrdenCompra: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    identificadorProveedor: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    estadoOrden: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const initial = initColumnFilters();
    const estadoOrden = Number(searchParams.get("estadoOrden"));
    return ESTADOS_ORDEN_URL.includes(estadoOrden)
      ? {
          ...initial,
          estadoOrden: {
            value: estadoOrden as any,
            matchMode: FilterMatchMode.EQUALS,
          },
        }
      : initial;
  });

  // Modal States
  const [selectedInvoiceForSii, setSelectedInvoiceForSii] =
    useState<InvoiceItem | null>(null);
  const [selectedParentOrdenForSii, setSelectedParentOrdenForSii] =
    useState<OrdenCompraGroupedResponse | null>(null);
  const [showSiiModal, setShowSiiModal] = useState<boolean>(false);

  // Dropdown Options
  const tipoOrdenOptions = [
    { label: "Stock", value: 1 },
    { label: "Servicios", value: 2 },
    { label: "Activos/Inversiones", value: 3 },
  ];

  const estadoOrdenOptions = [
    { label: "EMITIDA", value: 2 },
    { label: "PARCIALMENTE CUMPLIDA", value: 3 },
    { label: "CUMPLIDA", value: 4 },
    { label: "ANULADA", value: 5 },
  ];

  const tipoDocumentoOptions = [
    { label: "70 - Factura Electrónica", value: 70 },
    { label: "71 - Nota de Crédito", value: 71 },
    { label: "72 - Nota de Débito", value: 72 },
    { label: "77 - Factura Exenta", value: 77 },
  ];

  const estadoFacturaOptions = [
    { label: "CUADRADO", value: "CUADRADO" },
    { label: "CUADRADO CON REBAJE", value: "CUADRADO CON REBAJE" },
    { label: "DESCUADRADO", value: "DESCUADRADO" },
  ];

  const estadoPagoOptions = [
    { label: "PAGADO", value: "PAGADA" },
    { label: "PENDIENTE", value: "PENDIENTE" },
    { label: "ANULADO", value: "ANULADA" },
    { label: "PROCESADO", value: "PROCESADA" },
  ];

  const openSiiModal = async (
    invoice: InvoiceItem,
    parentOrden: OrdenCompraGroupedResponse,
  ) => {
    const invoiceNum = invoice.numeroFacturaLegal || invoice.numeroFacturaRea;
    const rutProveedor =
      invoice.identificadorProveedorFactura ||
      parentOrden.identificadorProveedor;

    if (!invoiceNum || !rutProveedor) {
      showMsg(
        toast,
        "warning",
        "Datos incompletos",
        "No se encontró el RUT de proveedor o número de factura necesario para consultar el documento.",
      );
      return;
    }

    // 1. Open a blank window synchronously to prevent browser popup blockers
    const newTab = window.open("about:blank", "_blank");

    try {
      // 2. Fetch the Base64 on demand from the new endpoint
      const base64 = await api.GET_DTE_PDF_INVOICE.getDtePdf(
        rutProveedor,
        invoiceNum,
      );

      if (!base64) {
        if (newTab) newTab.close();
        showMsg(
          toast,
          "warning",
          "PDF no disponible",
          `No existe un documento PDF cargado para la factura N° ${invoiceNum}.`,
        );
        return;
      }

      // 3. Convert base64 to PDF Blob Object URL
      const blob = base64ToBlob(base64, "application/pdf");
      const pdfUrl = URL.createObjectURL(blob);

      // 4. Load the PDF inside the opened tab
      if (newTab) {
        newTab.location.href = pdfUrl;
      } else {
        window.open(pdfUrl, "_blank");
      }
    } catch (error) {
      if (newTab) newTab.close();
      console.error("Error opening DTE PDF:", error);
      showMsg(
        toast,
        "danger",
        "Error PDF",
        `No se pudo recuperar el PDF para la factura N° ${invoiceNum}.`,
      );
    }
  };

  /*const openSiiModal = (
    invoice: InvoiceItem,
    parentOrden: OrdenCompraGroupedResponse,
  ) => {
    if (!invoice.pdfUrl) {
      showMsg(
        toast,
        "warning",
        "PDF no disponible",
        `No existe un documento PDF cargado para la factura N° ${
          invoice.numeroFacturaLegal || invoice.numeroFacturaRea
        }.`,
      );
      return;
    }

    const base64Data = invoice.pdfUrl
      .replace(/^data:application\/pdf;base64,/, "")
      .trim();

    try {
      const pdfWindow = window.open();
      if (pdfWindow) {
        pdfWindow.document.write(
          `<iframe src="data:application/pdf;base64,${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`,
        );
        pdfWindow.document.title = `Factura_${invoice.numeroFacturaLegal || invoice.numeroFacturaRea}.pdf`;
      } else {
        showMsg(
          toast,
          "danger",
          "Bloqueador de ventanas emergentes",
          "Por favor, permita las ventanas emergentes para visualizar el PDF.",
        );
      }
    } catch (error) {
      console.error("Error opening PDF Base64:", error);
      showMsg(
        toast,
        "danger",
        "Error PDF",
        "Ocurrió un error al procesar el archivo PDF.",
      );
    }
  };*/

  /*const fetchOrdenesConsolidadoContable = async () => {
    setLoading(true);
    
    const responseGetOrdenesConsolidado =
      await api.GET_ORDENES_FACTURAS_PAGOS.getOrdenesFacturasPagos(
        initData.id,
        initData.centros_costo_vista,
      );

    if (responseGetOrdenesConsolidado !== null) {
      ordenesFacturasPagos.current = responseGetOrdenesConsolidado;
      setData(responseGetOrdenesConsolidado);
    } else {
      showMsg(
        toast,
        "danger",
        "Error",
        "Error capturando datos de ordenes facturas y pagos",
      );
    }

    const responseGetOrdenesConsolidado =
      await api.GET_ORDENES_FACTURAS_PAGINATED.getOrdenesPaginated(1, 500);

    if (responseGetOrdenesConsolidado !== null) {
      ordenesFacturasPagos.current = responseGetOrdenesConsolidado;
      setData(responseGetOrdenesConsolidado);
    } else {
      showMsg(
        toast,
        "danger",
        "Error",
        "Error capturando datos de ordenes facturas y pagos",
      );
    }
    setLoading(false);
  };*/

  const fetchOrdenesConsolidadoContable = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    const PAGE_LIMIT = 500;

    try {
      // -------------------------------------------------------------
      // Step 1: Immediate fetch for Page 1 (renders in < 300ms)
      // -------------------------------------------------------------
      const firstResponse =
        await api.GET_ORDENES_FACTURAS_PAGINATED.getOrdenesPaginated(
          1,
          PAGE_LIMIT,
        );

      let firstList: OrdenCompraGroupedResponse[] = [];
      let hasMore = false;

      if (firstResponse) {
        firstList = Array.isArray(firstResponse)
          ? firstResponse
          : Array.isArray(firstResponse.data)
            ? firstResponse.data
            : [];
        hasMore =
          firstResponse.hasMore !== undefined
            ? Boolean(firstResponse.hasMore)
            : firstList.length === PAGE_LIMIT;
      }

      // Display the first 500 records immediately and remove table spinner
      setData(firstList);
      ordenesFacturasPagos.current = firstList;
      setLoading(false);

      // -------------------------------------------------------------
      // Step 2: Background worker for Pages 2, 3, 4...
      // -------------------------------------------------------------
      if (hasMore) {
        setBackgroundSyncing(true);
        let currentPage = 2;

        while (hasMore) {
          const nextResponse =
            await api.GET_ORDENES_FACTURAS_PAGINATED.getOrdenesPaginated(
              currentPage,
              PAGE_LIMIT,
            );

          if (!nextResponse) break;

          const nextList: OrdenCompraGroupedResponse[] = Array.isArray(
            nextResponse,
          )
            ? nextResponse
            : Array.isArray(nextResponse.data)
              ? nextResponse.data
              : [];

          if (nextList.length === 0) {
            hasMore = false;
            break;
          }

          // Append each new batch seamlessly to table state
          setData((prevData) => {
            const updated = [...prevData, ...nextList];
            ordenesFacturasPagos.current = updated;
            return updated;
          });

          hasMore =
            nextResponse.hasMore !== undefined
              ? Boolean(nextResponse.hasMore)
              : nextList.length === PAGE_LIMIT;

          currentPage++;
        }
      }
    } catch (error) {
      console.error("Error fetching ordenes progresivamente:", error);
      showMsg(
        toast,
        "danger",
        "Error",
        "Error al sincronizar datos de órdenes y facturas",
      );
    } finally {
      setLoading(false);
      setBackgroundSyncing(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchOrdenesConsolidadoContable();
  }, []);

  // --- Action to Mark Order as Cumplida ---
  const handleMarcarCumplidaAction = async (
    orden: OrdenCompraGroupedResponse,
  ) => {
    try {
      console.log(orden.fechaOrden);
      if (!USUARIOS_FINANZAS.includes(initData.id)) {
        showMsg(
          toast,
          "warning",
          "Aviso",
          `Solo finanzas puede marcar la Orden N° ${orden.nroOrdenCompra} como cumplida...`,
        );
      } else if (orden.tipoOrden !== 2) {
        showMsg(
          toast,
          "warning",
          "Aviso",
          `Solo se pueden cumplir las ordenes de tipo servicio en este módulo.`,
        );
      } else {
        //RECOPILACION DE LOS DATOS
        const now = new Date();
        const dateOrden = new Date(orden.fechaOrden);
        const ano = now.getFullYear();
        const mes = now.getMonth() + 1;
        const mesPadded = String(now.getMonth() + 1).padStart(2, "0");
        const anoStr = String(now.getFullYear());
        const anoOrden = dateOrden.getFullYear();
        const mesOrden = dateOrden.getMonth() + 1;

        //POR CADA FACTURA VA OBJETO COMPLETO
        let fechaFacturaUltima = "";
        let horaUltimaFactura = "";
        let glosa = "";
        let glosa2 = "";
        for (const factura of orden.facturas ?? []) {
          glosa = glosa + `F-${factura.numeroFacturaLegal},`;
          glosa2 = glosa2 + `C-${factura.numeroComprobante},`;
          fechaFacturaUltima = factura.fechaFactura ?? "";
          horaUltimaFactura = new Date(factura.fechaFactura ?? "01-01-1753")
            .toISOString()
            .slice(11, 19);
        }

        let objRecepcion = {
          adqRcSuc: 1,
          adqRcAno: ano,
          adqRcMes: mes,
          adqRcTip:
            orden.ocSubdocId == 1
              ? 2
              : orden.ocSubdocId == 2
                ? 1
                : orden.ocSubdocId, //STOCK 1, SERVICIO 2, INVERSIONES 3
          adqRcFch: now,
          adqRcOdSu: 1,
          adqRcOdAn: anoOrden,
          adqRcOdMe: mesOrden,
          adqRcOdNu: orden.nroOrdenCompra,
          adqRcOdFe: dateOrden,
          adqRcTLin: orden?.detallesOrden?.length ?? 0,
          prvTip: 1,
          prvCod: orden.identificadorProveedor,
          prvSec: 0,
          adqRcPrvN: orden.proveedorNombre, //NOMBRE PROVEEDOR
          adqRcEst: 2, //RECEPCION COMPLETADA DE ESTA FACTURA
          adqRcMoCo: orden.ocTipmonId !== 40 ? orden.ocTipmonId : 41,
          adqRcAuUs: initData.usu_login,
          adqRcAuFc: fechaFacturaUltima ?? "01-01-1753",
          adqRcAuHr: horaUltimaFactura,
          adqRcBodC: 0,
          adqRcMeTm: 0,
          adqRcMeCm: 0,
          adqRcMeSu: 0,
          adqRcMeNu: 0,
          adqRcTaCa: orden.ocTasaCambio, // EN BASE A LA ORDEN
          adqRcTaAp: 1,
          adqRcRef1: "",
          adqRcRef2: "",
          adqRcGlo1: glosa, //VER QUE GLOSA DEJAR F-298 C-1020093 $490.000
          adqRcGlo2: glosa2, //INYECAR NUMERO FACTURA O NUMEROS FACTURAS F-298 C-1020093 $490.000
          adqRcCbAm: 0, // ESTO LO LLENA EL CIERRE
          adqRcCbNu: 0, // ESTO LO LLENA EL CIERRE
          adqRcCbFc: "01-01-1753", // ESTO LO LLENA EL CIERRE
          adqRcLbFc: 0,
          adqRcLbNm: 0,
          adqRcLbFh: "01-01-1753",
          adqRcAfSu: 0,
          adqRcAfNu: 0,
          adqRcAfPe: 0,
          adqRcFor: 1,
          adqRcFaTi: 0,
          adqRcFaNu: 0,
          detalles: [] as any[],
        };

        //COMO SE HACE ESTO CON SERVICIOS
        //ESTO PUEDE SER UN FOR
        let count = 1;
        for (const detalle of orden.detallesOrden ?? []) {
          const objRecepcionDetalle: any = {
            adqRcSuc: 1,
            adqRcAno: ano,
            adqRcMes: mes,
            adqRcTip:
              orden.ocSubdocId == 1
                ? 2
                : orden.ocSubdocId == 2
                  ? 1
                  : orden.ocSubdocId,
            adqRlLin: count,
            adqRlOlLi: count,
            adqRlQRec: detalle.cantidad, //cantidad
            adqRlQFor: 0.0,
            adqRlQDev: 0.0,
            adqRlQSol: 0.0,
            prdCod: detalle.itemCodigo,
            adqRlPdNo: detalle.itemNombre,
            adqRlPrMo: detalle.precioUnitario, //precio unitario
            adqRlVal: detalle.precioTotal, //precio total
            adqRlSTot: detalle.cantidad,
            adqRlSBod: detalle.cantidad,
            adqRlPTot: detalle.cantidad,
            adqRlPBod: detalle.cantidad,
            adqRlAfPe: 0,
            adqRlAfNu: 0,
            adqRlAfSu: 0,
            adqRlVInf: 0,
            adqRlQRAd: detalle.cantidad,
            adqRlFaPr: 0,
            adqRlPrIm: null,
          };
          objRecepcion.detalles.push(objRecepcionDetalle);
          count++;
        }
        //SE ENVIA UNA SOLA RECEPCION AL INET INCLUYENDO TODAS SUS FACTURAS
        const responseAddRecepcion: any =
          await api.SEND_RECEPCION_INET.sendRecepcionInet(
            JSON.stringify(objRecepcion),
          );

        if (responseAddRecepcion) {
          //ACTUALIZA ESTADO
          let objUpdateOrdenEstado = {
            adqOdNum: orden.nroOrdenCompra,
            adqOdEst: 4,
          };
          const updateOrden =
            await api.POST_UPDATE_ORDEN_COMPRA_ESTADO_INET.postOrdenCompraEstadoInet(
              JSON.stringify(objUpdateOrdenEstado),
            );

          if (updateOrden) {
            showMsg(
              toast,
              "success",
              "Recepción registrada",
              `La Orden de Compra N° ${orden.nroOrdenCompra} fue marcada cumplida.`,
            );
          } else {
            showMsg(
              toast,
              "danger",
              "No se logró registrar la recepción",
              `La Orden de Compra N° ${orden.nroOrdenCompra} no pudo ingresar la recepción.`,
            );
          }
          return responseAddRecepcion;
        }
      }
    } catch (error) {
      console.error("Error cumpliendo orden:", error);
      showMsg(
        toast,
        "danger",
        "Error",
        "Ocurrió un error al intentar cambiar el estado de la Orden de Compra.",
      );
    }
  };

  const updateRecepcionArrayWithNewRecepcionInet = async (
    orden: OrdenCompraGroupedResponse,
    responseAddRecepcion: any,
  ) => {
    const objRecepcionWeb = {
      nroOrdenCompra: Number(orden.nroOrdenCompra),
      numeroFacturaLegal: Number(orden.facturas?.[0]?.numeroFacturaLegal || 0),
      nuevaRecepcion: JSON.stringify({
        AdqRcNum:
          responseAddRecepcion?.AdqRcNum ?? responseAddRecepcion?.adqRcNum ?? 0,
        AdqRcOdNu:
          responseAddRecepcion?.AdqRcOdNu ??
          responseAddRecepcion?.adqRcOdNu ??
          orden.nroOrdenCompra,
        AdqRcFch: responseAddRecepcion?.AdqRcFch ?? new Date().toISOString(),
        AdqRcGlo1:
          responseAddRecepcion?.AdqRcGlo1 ??
          responseAddRecepcion?.adqRcGlo1 ??
          "",
        AdqRcGlo2:
          responseAddRecepcion?.AdqRcGlo2 ??
          responseAddRecepcion?.adqRcGlo2 ??
          "",
      }),
    };

    //ACTUALIZAR ESTADO ORDEN Y ACTUALIZAR RECEPCIONES EN recepciones_documentos
    //API NORMAL
    const finalResponse =
      await api.ADD_RECEPCION_DOCUMENTO.addRecepcionDocumento(
        JSON.stringify(objRecepcionWeb),
      );

    if (finalResponse) {
      console.log(
        "Recepción actualizada en recepciones_documentos:",
        finalResponse,
      );
      fetchOrdenesConsolidadoContable(); // Refresh the table data after updating
    }
  };

  const confirmMarcarCumplida = (orden: OrdenCompraGroupedResponse) => {
    confirmDialog({
      message: "¿Está seguro de hacer cumplir esta orden de compra?",
      header: `Confirmación - Orden N° ${orden.nroOrdenCompra}`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, Cumplir",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-success",
      accept: async () => {
        try {
          const res: any = await handleMarcarCumplidaAction(orden);

          // If your action returns a falsy value when failing internally
          if (!res) return;

          await updateRecepcionArrayWithNewRecepcionInet(orden, res.data);

          /*showMsg(
            toast,
            "success",
            "Éxito",
            `La Orden N° ${orden.nroOrdenCompra} fue marcada como cumplida correctamente.`,
          );*/
        } catch (error: any) {
          console.error("Error cumpliendo orden:", error);

          const status = error?.response?.status;
          const errorMessage = GetHttpErrorMessage(error);

          let errorTitle = "Error";
          if (status === 400) errorTitle = "Datos Inválidos (400)";
          else if (status === 404) errorTitle = "No Encontrado (404)";
          else if (status === 500) errorTitle = "Error de Servidor (500)";

          showMsg(toast, "danger", errorTitle, errorMessage);
        }
      },
    });
  };

  // --- Action to Mark Order as Semi Cumplida (INET state 3) ---
  // Same permission rules as "cumplida", but it only changes the order state
  // in INET: no reception is sent and nothing is written to
  // recepciones_documentos
  const handleMarcarSemiCumplidaAction = async (
    orden: OrdenCompraGroupedResponse,
  ) => {
    if (!USUARIOS_FINANZAS.includes(initData.id)) {
      showMsg(
        toast,
        "warning",
        "Aviso",
        `Solo finanzas puede marcar la Orden N° ${orden.nroOrdenCompra} como semi cumplida...`,
      );
      return;
    }
    if (orden.tipoOrden !== 2) {
      showMsg(
        toast,
        "warning",
        "Aviso",
        `Solo se pueden marcar semi cumplidas las ordenes de tipo servicio en este módulo.`,
      );
      return;
    }
    try {
      const objUpdateOrdenEstado = {
        adqOdNum: orden.nroOrdenCompra,
        adqOdEst: 3, // PARCIALMENTE CUMPLIDA
      };
      const updateOrden =
        await api.POST_UPDATE_ORDEN_COMPRA_ESTADO_INET.postOrdenCompraEstadoInet(
          JSON.stringify(objUpdateOrdenEstado),
        );
      if (updateOrden) {
        showMsg(
          toast,
          "success",
          "Orden actualizada",
          `La Orden de Compra N° ${orden.nroOrdenCompra} fue marcada semi cumplida.`,
        );
        fetchOrdenesConsolidadoContable(); // Refresh the table data
      }
    } catch (error: any) {
      console.error("Error marcando orden semi cumplida:", error);
      showMsg(
        toast,
        "danger",
        error?.status ? `Error (${error.status})` : "Error",
        error?.message ||
          "Ocurrió un error al intentar cambiar el estado de la Orden de Compra.",
      );
    }
  };

  const confirmMarcarSemiCumplida = (orden: OrdenCompraGroupedResponse) => {
    confirmDialog({
      message:
        "¿Está seguro de marcar esta orden de compra como semi cumplida (parcialmente cumplida)?",
      header: `Confirmación - Orden N° ${orden.nroOrdenCompra}`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, marcar semi cumplida",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-warning",
      accept: () => handleMarcarSemiCumplidaAction(orden),
    });
  };

  // --- Custom Filter Logic (Invoice-Level Filters) ---
  /*const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (filterNumFactura && filterNumFactura.trim() !== "") {
        const exactSearch = filterNumFactura.trim();
        const hasExactInvoiceMatch = row.facturas.some(
          (inv) =>
            inv.numeroFacturaLegal?.toString() === exactSearch ||
            inv.numeroFacturaRea?.toString() === exactSearch,
        );
        if (!hasExactInvoiceMatch) return false;
      }

      if (filterTipoDocumento !== null && filterTipoDocumento !== undefined) {
        const hasMatchingTipoDoc = row.facturas.some(
          (inv) => inv.tipoDocumento === filterTipoDocumento,
        );
        if (!hasMatchingTipoDoc) return false;
      }

      if (filterEstadoFactura) {
        const hasMatchingEstadoFac = row.facturas.some(
          (inv) => inv.estadoFactura === filterEstadoFactura,
        );
        if (!hasMatchingEstadoFac) return false;
      }

      if (filterEstadoPago) {
        const hasMatchingEstadoPago = row.facturas.some(
          (inv) => inv.estadoPago === filterEstadoPago,
        );
        if (!hasMatchingEstadoPago) return false;
      }

      if (filterFechaFactura) {
        const filterDateStr = filterFechaFactura.toISOString().split("T")[0];
        const hasMatchingDate = row.facturas.some((inv) => {
          if (!inv.fechaFactura) return false;
          const invDateStr = new Date(inv.fechaFactura)
            .toISOString()
            .split("T")[0];
          return invDateStr === filterDateStr;
        });
        if (!hasMatchingDate) return false;
      }

      return true;
    });
  }, [
    data,
    filterNumFactura,
    filterTipoDocumento,
    filterEstadoFactura,
    filterEstadoPago,
    filterFechaFactura,
  ]);*/

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.filter((row) => {
      const facturas = row?.facturas || [];

      if (filterNumFactura && filterNumFactura.trim() !== "") {
        const exactSearch = filterNumFactura.trim();
        const hasExactInvoiceMatch = facturas.some(
          (inv) =>
            inv.numeroFacturaLegal?.toString() === exactSearch ||
            inv.numeroFacturaRea?.toString() === exactSearch,
        );
        if (!hasExactInvoiceMatch) return false;
      }

      if (filterTipoDocumento !== null && filterTipoDocumento !== undefined) {
        const hasMatchingTipoDoc = facturas.some(
          (inv) => inv.tipoDocumento === filterTipoDocumento,
        );
        if (!hasMatchingTipoDoc) return false;
      }

      if (filterEstadoFactura) {
        const hasMatchingEstadoFac = facturas.some(
          (inv) => inv.estadoFactura === filterEstadoFactura,
        );
        if (!hasMatchingEstadoFac) return false;
      }

      if (filterEstadoPago) {
        const hasMatchingEstadoPago = facturas.some(
          (inv) => inv.estadoPago === filterEstadoPago,
        );
        if (!hasMatchingEstadoPago) return false;
      }

      if (filterFechaFactura) {
        const filterDateStr = filterFechaFactura.toISOString().split("T")[0];
        const hasMatchingDate = facturas.some((inv) => {
          if (!inv.fechaFactura) return false;
          const invDateStr = new Date(inv.fechaFactura)
            .toISOString()
            .split("T")[0];
          return invDateStr === filterDateStr;
        });
        if (!hasMatchingDate) return false;
      }

      return true;
    });
  }, [
    data,
    filterNumFactura,
    filterTipoDocumento,
    filterEstadoFactura,
    filterEstadoPago,
    filterFechaFactura,
  ]);

  const clearFilters = () => {
    setFilterNumFactura("");
    setFilterTipoDocumento(null);
    setFilterEstadoFactura(null);
    setFilterEstadoPago(null);
    setFilterFechaFactura(null);
    setColumnFilters(initColumnFilters());
  };

  const tipoOrdenFilterTemplate = (options: any) => (
    <Dropdown
      value={options.value}
      options={tipoOrdenOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Seleccione Tipo"
      className="p-column-filter p-inputtext-sm"
      showClear
    />
  );

  const estadoOrdenFilterTemplate = (options: any) => (
    <Dropdown
      value={options.value}
      options={estadoOrdenOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Seleccione Estado"
      className="p-column-filter p-inputtext-sm"
      showClear
    />
  );

  const fetchDatosAprobacionesConfirmaciones = async (
    idOrdenCompra: number,
  ) => {
    const responseGetOrdenesStatesApproval =
      await api.GET_ORDENES_COMPRA_APPROVALS_STATES.getOrdenesCompraApprovalStates(
        idOrdenCompra,
      );
    if (responseGetOrdenesStatesApproval !== null) {
      ordenesCompraApprovalStates.current = responseGetOrdenesStatesApproval;
      return responseGetOrdenesStatesApproval;
    }
  };

  const fetchDatosFirmaAprobacionesConfirmaciones = async (idOrden: number) => {
    const responseGetSignOrdersStatesApproval =
      await api.GET_SIGN_ORDER_HISTORY.getSignOrderHistory(idOrden);
    if (responseGetSignOrdersStatesApproval !== null) {
      ordenesSignApprovalHistory.current = responseGetSignOrdersStatesApproval;
      return responseGetSignOrdersStatesApproval;
    }
  };

  const fetchDatosProveedorById = async (idProveedor: number) => {
    const responseGetDataProveedor =
      await api.GET_DATOS_PROVEEDORES_BY_ID.getDataProveedorById(idProveedor);
    if (responseGetDataProveedor) {
      dataProveedor.current = responseGetDataProveedor;
      return responseGetDataProveedor;
    }
    return null;
  };

  const fetchOrdenCompraById = async (idOrden: number) => {
    const responseGetOrdenCompra =
      await api.GET_ORDEN_COMPRA_BY_ID.getOrdenCompraById(idOrden);
    return responseGetOrdenCompra || null;
  };

  // Helper function to convert base64 to Blob
  const base64ToBlob = (
    base64: string,
    contentType = "application/pdf",
  ): Blob => {
    // Clean any prefix if present (e.g. data:application/pdf;base64,)
    const cleanBase64 = base64.replace(/^data:application\/pdf;base64,/, "");
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  /*const handleGeneratePdfOc = async (rowData: any) => {
    // 1. Open a blank window synchronously to prevent browser popup blockers
    const newTab = window.open("about:blank", "_blank");

    try {
      const base64 = await api.GET_DTE_PDF_INVOICE.getDtePdf(
        rowData.identificadorProveedor,
        rowData.numeroFacturaLegal,
      );

      if (!base64) {
        throw new Error("Base64 data not found");
      }

      // 2. Convert base64 to PDF Blob
      const blob = base64ToBlob(base64, "application/pdf");
      const pdfUrl = URL.createObjectURL(blob);

      // 3. Navigate the opened tab to the PDF URL
      if (newTab) {
        newTab.location.href = pdfUrl;
      } else {
        // Fallback if popup blocker still intercepted
        window.open(pdfUrl, "_blank");
      }
    } catch (error) {
      // Close the blank tab if the request failed
      if (newTab) newTab.close();

      showMsg(
        toast,
        "danger",
        "Error",
        "No se pudo abrir el PDF del documento.",
      );
    }
  };*/

  const handleGeneratePdfOc = async (dataOc: OrdenCompraGroupedResponse) => {
    let dataOrden: any = {};
    fetchOrdenCompraById(dataOc.nroOrdenCompra).then((ordenCompraData) => {
      dataOrden = ordenCompraData;
      fetchDatosAprobacionesConfirmaciones(dataOc.nroOrdenCompra).then(() => {
        fetchDatosFirmaAprobacionesConfirmaciones(dataOc.nroOrdenCompra).then(
          () => {
            fetchDatosProveedorById(dataOrden.prov_id).then(() => {
              PdfCreator({
                data: dataOrden,
                initData: initData,
                ordenesApprovalStates:
                  ordenesCompraApprovalStates.current ?? [],
                solicitudesSignApprovalHistory:
                  ordenesSignApprovalHistory.current ?? [],
                dataProveedor: dataProveedor.current ?? {},
                parametros: parametros,
              });
            });
          },
        );
      });
    });
  };

  /**** SECCION EXCEL *****/
  const saveAsExcelFile = (buffer: BlobPart, fileName: string) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const dataBlob = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          dataBlob,
          fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION,
        );
      }
    });
  };

  const exportConsolidadoExcel = () => {
    import("xlsx").then((xlsx) => {
      const today = new Date().toLocaleDateString("es-ES").split("/").join("");

      const safeParse = (jsonStr: any) => {
        if (!jsonStr) return [];
        if (typeof jsonStr === "object") return jsonStr;
        try {
          return JSON.parse(jsonStr);
        } catch {
          return [];
        }
      };

      const sheetCabecera = xlsx.utils.json_to_sheet(
        filteredData.map((item) => ({
          "Tipo Orden": item.tipoOrden,
          "N° Orden Compra": item.nroOrdenCompra,
          "Monto Neto OC": item.montoTotalNetoOrden,
          "Estado Orden": item.estadoOrden,
          "RUT Proveedor OC": item.identificadorProveedor,
          "Fecha Mov. Contable": item.fechaMovimientoContable
            ? new Date(item.fechaMovimientoContable).toLocaleDateString("es-CL")
            : "",
          "Cantidad Facturas Asociadas": item.facturas
            ? item.facturas.length
            : 0,
        })),
      );

      const sheetFacturas = xlsx.utils.json_to_sheet(
        filteredData.flatMap((item) => {
          const facturasList = item.facturas || [];
          return facturasList.map((inv) => ({
            "N° Orden Compra": item.nroOrdenCompra,
            "Tipo Documento": inv.tipoDocumento
              ? `${inv.tipoDocumento} - ${inv.nombreDocumento}`
              : "-",
            "N° Factura Legal": inv.numeroFacturaLegal || "-",
            "N° Factura Real": inv.numeroFacturaRea || "-",
            "N° Comprobante": inv.numeroComprobante || "-",
            "RUT Proveedor Factura":
              inv.identificadorProveedorFactura ||
              item.identificadorProveedor ||
              "-",
            "Fecha Factura": inv.fechaFactura
              ? new Date(inv.fechaFactura).toLocaleDateString("es-CL")
              : "-",
            "Glosa Factura": inv.facturaGlosa?.trim() || "-",
            "N° Comprobante Ref.": inv.numeroComprobanteReferencia || "-",
            "Monto Factura": inv.montoFactura || 0,
            "Monto ConHis": inv.montoConHis || 0,
            "Estado Factura": inv.estadoFactura || "-",
            "Estado Pago": inv.estadoPago || "-",
          }));
        }),
      );

      const sheetRecepciones = xlsx.utils.json_to_sheet(
        filteredData.flatMap((item) => {
          return (item.facturas || []).flatMap((inv) => {
            const recepciones = safeParse(inv.recepcionesArray);
            return recepciones.map((rec: any) => ({
              "N° Orden Compra": item.nroOrdenCompra,
              "N° Factura Legal": inv.numeroFacturaLegal,
              "N° Recepción": rec.AdqRcNum,
              "Fecha Recepción": rec.AdqRcFch
                ? new Date(rec.AdqRcFch).toLocaleDateString("es-CL")
                : "",
              "Glosa 1": rec.AdqRcGlo1?.trim(),
              "Glosa 2": rec.AdqRcGlo2?.trim(),
            }));
          });
        }),
      );

      const sheetFacturaDetalle = xlsx.utils.json_to_sheet(
        filteredData.flatMap((item) => {
          return (item.facturas || []).flatMap((inv) => {
            const detalles = safeParse(inv.facturaDetalleArray);
            return detalles.map((det: any) => ({
              "N° Orden Compra": item.nroOrdenCompra,
              "N° Factura Legal": inv.numeroFacturaLegal,
              "N° Factura Real": inv.numeroFacturaRea,
              Concepto: det.FcoTotNom?.trim(),
              Monto: det.FcoTotVal,
            }));
          });
        }),
      );

      const sheetConhis = xlsx.utils.json_to_sheet(
        filteredData.flatMap((item) => {
          return (item.facturas || []).flatMap((inv) => {
            const conhis = safeParse(inv.conhisArray);
            return conhis.map((ch: any) => ({
              "N° Comprobante": inv.numeroComprobante,
              "N° Orden Compra": item.nroOrdenCompra,
              "N° Factura Legal": inv.numeroFacturaLegal,
              "Tipo Movimiento": ch.tipo_movimiento,
              "Cuenta Contable": ch.cuenta_contable?.trim(),
              "Centro Costo": ch.centro_costo,
              "Monto Nacional": ch.monto_nacional,
              "Monto Otro": ch.monto_otro,
              "Glosa Contable": ch.glosa?.trim(),
            }));
          });
        }),
      );

      const workbook = {
        Sheets: {
          "Consolidado General": sheetCabecera,
          Facturas: sheetFacturas,
          Recepciones: sheetRecepciones,
          "Detalle Factura": sheetFacturaDetalle,
          "Historial Contable": sheetConhis,
        },
        SheetNames: [
          "Consolidado General",
          "Facturas",
          "Recepciones",
          "Detalle Factura",
          "Historial Contable",
        ],
        Props: {},
      };

      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, `Consolidado_Contable_OC_${today}`);
    });
  };

  const payInvoice = (rowData: InvoiceItem) => {
    showMsg(
      toast,
      "info",
      "Pago en proceso",
      `Se está procesando el pago de la factura N° ${rowData.numeroFacturaLegal}.`,
    );
  };

  const cardTitleHeader = (
    invoiceRow: InvoiceItem,
    ordenCompraData: OrdenCompraGroupedResponse,
  ) => {
    return (
      <div className="flex justify-content-between align-items-center w-full">
        <span>Detalle Factura</span>

        {/*invoiceRow.estadoPago === "PENDIENTE" ? (
          <Button
            label="PAGAR"
            icon="pi pi-credit-card"
            className="p-button-sm p-button-outlined p-button-success border-round-3xl font-semibold shadow-1 hover:shadow-3 transition-all transition-duration-200"
            style={{
              marginLeft: "20px",
              borderWidth: "2px",
              padding: "0.35rem 1rem",
            }}
            onClick={() => payInvoice(invoiceRow)}
          />
        ) : null>*/}
      </div>
    );
  };

  const nroOrdenCompraTemplate = (rowData: OrdenCompraGroupedResponse) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap", // <-- Placed on parent container to allow wrapping on zoom
        alignItems: "center",
        justifyContent: "flex-start", // <-- Left-aligned so elements stack naturally
        gap: "0.35rem",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          fontWeight: 600,
          fontSize: "0.85rem",
          lineHeight: "1.2",
          whiteSpace: "nowrap", // <-- Prevents digits in the number from splitting
        }}
      >
        {rowData.nroOrdenCompra}
      </span>
      <Button
        label="PDF ↓"
        className="p-button-sm btn-cinematic"
        style={{
          fontSize: "0.72rem",
          padding: "0.15rem 0.4rem",
          lineHeight: 1,
          height: "auto",
          whiteSpace: "nowrap",
          maxWidth: "1rem",
          minWidth: "fit-content",
        }}
        onClick={() => {
          SHOW_MSG_ACTION(
            toast,
            "warning",
            "Advertencia",
            "Los protocolos internos prohíben enviar ordenes de compra a proveedores.",
            () => handleGeneratePdfOc(rowData),
          );
        }}
      />
    </div>
  );

  const nroFacturaTemplate = (
    invoice: InvoiceItem,
    parentOrden: OrdenCompraGroupedResponse,
  ) => (
    <div style={{ whiteSpace: "nowrap" }}>
      <span
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          marginRight: "0.3rem",
        }}
      >
        {invoice.numeroFacturaLegal || "-"} / {invoice.numeroFacturaRea || "-"}
      </span>
      {invoice.numeroFacturaLegal ? (
        <Button
          label="SII ↓"
          className="btn-cinematic"
          style={{
            display: "inline-block",
            verticalAlign: "middle",
            whiteSpace: "nowrap",
            width: "auto",
            padding: "0.1rem 0.3rem",
            maxWidth: "3rem",
          }}
          onClick={() => openSiiModal(invoice, parentOrden)}
        />
      ) : null}
    </div>
  );

  const currencyFormatter = (value?: number) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(value || 0);

  const estadoOcTemplate = (rowData: OrdenCompraGroupedResponse) => {
    let label = "Desconocido";
    let severity: "success" | "info" | "warning" | "danger" | "secondary" =
      "secondary";

    switch (rowData.estadoOrden) {
      case 2:
        label = "EMITIDA";
        severity = "info";
        break;
      case 3:
        label = "PARCIALMENTE CUMPLIDA";
        severity = "warning";
        break;
      case 4:
        label = "CUMPLIDA";
        severity = "success";
        break;
      case 5:
        label = "ANULADA";
        severity = "danger";
        break;
    }

    return <Tag value={label} severity={severity} />;
  };

  const estadoFacTemplate = (invoice: InvoiceItem) => (
    <Tag
      value={invoice.estadoFactura || "N/A"}
      severity={invoice.estadoFactura === "CUADRADO" ? "success" : "danger"}
    />
  );

  const estadoConhisTemplate = (invoice: InvoiceItem) => {
    let severity: "success" | "info" | "warning" | "danger" | "secondary" =
      "secondary";

    switch (invoice.estadoPago) {
      case "PAGADA":
        severity = "info";
        break;
      case "ANULADA":
        severity = "danger";
        break;
      case "PROCESADA":
        severity = "info";
        break;
      case "PENDIENTE":
        severity = "secondary";
        break;
    }

    return <Tag value={invoice.estadoPago} severity={severity} />;
  };

  const parseJsonArray = <T,>(jsonString?: string): T[] => {
    try {
      return jsonString ? JSON.parse(jsonString) : [];
    } catch {
      return [];
    }
  };

  const header = (
    <div className="flex flex-column gap-3 w-full py-2 px-1">
      {/* --- Top Row: Titles & Actions --- */}
      <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
        {/* Title & Description */}
        <div className="flex flex-column gap-1">
          <div className="flex flex-wrap align-items-center gap-2">
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#1e293b",
                letterSpacing: "-0.015em",
                margin: "10px",
              }}
            >
              {contextmenu === "pagos"
                ? "Visualización y pagos de facturas asociadas a órdenes de compra"
                : "Mantención de facturas asociadas a órdenes de compra"}
            </h2>
            {backgroundSyncing && (
              <Tag
                severity="info"
                value={`Sincronizando (${data.length} cargados)...`}
                icon="pi pi-spin pi-spinner"
                className="border-round-2xl px-2 py-1 shadow-1"
                style={{ fontSize: "0.75rem", fontWeight: 500 }}
              />
            )}
          </div>
          {(contextmenu === "mantiene" || contextmenu === "pagos") && (
            <p
              style={{
                color: "#64748b",
                fontSize: "0.875rem",
                lineHeight: 1.4,
                margin: "0 10px 10px 10px",
              }}
            >
              Visualice asociaciones de órdenes de compra con documentos
              fiscales, consulte PDFs en línea y exporte reportes detallados.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex align-items-center gap-2 flex-shrink-0 align-self-end md:align-self-center">
          <Button
            label="Exportar Excel"
            icon="pi pi-file-excel"
            className="p-button-success p-button-sm font-semibold shadow-1 hover:shadow-2"
            style={{
              height: "36px",
              padding: "0 1rem",
              borderRadius: "6px",
              marginRight: "0.5rem",
            }}
            onClick={exportConsolidadoExcel}
          />
          <Button
            label="Limpiar Filtros"
            icon="pi pi-filter-slash"
            className="p-button-outlined p-button-secondary p-button-sm font-semibold"
            style={{
              height: "36px",
              padding: "0 0.85rem",
              borderRadius: "6px",
              borderColor: "#cbd5e1",
              color: "#475569",
            }}
            onClick={clearFilters}
          />
        </div>
      </div>

      {/* --- Filter Bar Panel --- */}
      <div
        className="surface-card border-1 border-200 border-round-xl p-3 shadow-1"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.85rem",
          alignItems: "flex-end",
          backgroundColor: "#f8fafc",
        }}
      >
        {/* N° Factura */}
        <div className="flex flex-column gap-1">
          <label
            htmlFor="filterNumFactura"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              letterSpacing: "0.02em",
            }}
          >
            N° Factura:
          </label>
          <InputText
            id="filterNumFactura"
            value={filterNumFactura}
            onChange={(e) => setFilterNumFactura(e.target.value)}
            className="p-inputtext-sm w-full"
            placeholder="Ej: 1234"
            style={{
              height: "36px",
              fontSize: "0.825rem",
              borderRadius: "6px",
            }}
          />
        </div>

        {/* Tipo Documento */}
        <div className="flex flex-column gap-1">
          <label
            htmlFor="filterTipoDocumento"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              letterSpacing: "0.02em",
            }}
          >
            Tipo Documento:
          </label>
          <Dropdown
            id="filterTipoDocumento"
            value={filterTipoDocumento}
            options={tipoDocumentoOptions}
            onChange={(e) => setFilterTipoDocumento(e.value ?? null)}
            placeholder="Seleccione Tipo"
            className="p-inputtext-sm w-full"
            style={{
              height: "36px",
              fontSize: "0.825rem",
              borderRadius: "6px",
            }}
            showClear
          />
        </div>

        {/* Fecha Factura */}
        <div className="flex flex-column gap-1">
          <label
            htmlFor="filterFechaFactura"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              letterSpacing: "0.02em",
            }}
          >
            Fecha Factura:
          </label>
          <Calendar
            id="filterFechaFactura"
            value={filterFechaFactura}
            onChange={(e) => setFilterFechaFactura(e.value as Date)}
            dateFormat="dd/mm/yy"
            placeholder="dd/mm/aaaa"
            showIcon
            className="p-inputtext-sm w-full"
            style={{ height: "36px", fontSize: "0.825rem" }}
            inputStyle={{
              height: "36px",
              fontSize: "0.825rem",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
        </div>

        {/* Estado Factura */}
        <div className="flex flex-column gap-1">
          <label
            htmlFor="filterEstadoFactura"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              letterSpacing: "0.02em",
            }}
          >
            Estado Factura:
          </label>
          <Dropdown
            id="filterEstadoFactura"
            value={filterEstadoFactura}
            options={estadoFacturaOptions}
            onChange={(e) => setFilterEstadoFactura(e.value)}
            placeholder="Seleccione Estado"
            className="p-inputtext-sm w-full"
            style={{
              height: "36px",
              fontSize: "0.825rem",
              borderRadius: "6px",
            }}
            showClear
          />
        </div>

        {/* Estado Pago */}
        <div className="flex flex-column gap-1">
          <label
            htmlFor="filterEstadoPago"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              letterSpacing: "0.02em",
            }}
          >
            Estado Pago:
          </label>
          <Dropdown
            id="filterEstadoPago"
            value={filterEstadoPago}
            options={estadoPagoOptions}
            onChange={(e) => setFilterEstadoPago(e.value)}
            placeholder="Seleccione Pago"
            className="p-inputtext-sm w-full"
            style={{
              height: "36px",
              fontSize: "0.825rem",
              borderRadius: "6px",
            }}
            showClear
          />
        </div>
      </div>
    </div>
  );

  const invoiceRowExpansionTemplate = (
    invoice: InvoiceItem,
    parentOrden: OrdenCompraGroupedResponse,
  ) => {
    const recepciones: Recepcion[] = parseJsonArray(invoice.recepcionesArray);
    const facturaDetalle: FacturaDetalle[] = parseJsonArray(
      invoice.facturaDetalleArray,
    );
    const conhis: ConhisItem[] = parseJsonArray(invoice.conhisArray);

    return (
      <div className="p-3 bg-bluegray-50 border-round my-2 border-1 border-300">
        <h6 className="mt-0 mb-3 text-700">
          Detalles de la Factura N°{" "}
          {invoice.numeroFacturaLegal || invoice.numeroFacturaRea}
        </h6>
        <div className="grid">
          <div className="col-12 md:col-4">
            <Card title="Recepciones" className="shadow-1">
              <DataTable
                value={recepciones}
                size="small"
                emptyMessage="Sin recepciones"
              >
                <Column field="AdqRcNum" header="N° Rec." />
                <Column
                  field="AdqRcFch"
                  header="Fecha"
                  body={(r) =>
                    r.AdqRcFch
                      ? new Date(r.AdqRcFch).toLocaleDateString("es-CL")
                      : "-"
                  }
                />
                <Column field="AdqRcGlo1" header="Glosa" />
              </DataTable>
            </Card>
          </div>

          <div className="col-12 md:col-4">
            <Card
              title={cardTitleHeader(invoice, parentOrden)}
              className="shadow-1"
            >
              <DataTable
                value={facturaDetalle}
                size="small"
                emptyMessage="Sin detalle de factura"
              >
                <Column field="FcoTotNom" header="Concepto" />
                <Column
                  field="FcoTotVal"
                  header="Monto"
                  body={(d) => currencyFormatter(d.FcoTotVal)}
                />
              </DataTable>
            </Card>
          </div>

          <div className="col-12 md:col-4">
            <Card title="Historial Contable" className="shadow-1">
              <DataTable
                value={conhis}
                size="small"
                emptyMessage="Sin registros contables"
              >
                <Column field="tipo_movimiento" header="Tipo" />
                <Column field="cuenta_contable" header="Cuenta Contable" />
                <Column
                  field="monto_nacional"
                  header="Monto Nac."
                  body={(c) => currencyFormatter(c.monto_nacional)}
                />
              </DataTable>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // --- Primary Expansion Template for Purchase Orders ---
  const rowExpansionTemplate = (dataRow: OrdenCompraGroupedResponse) => {
    // Condition 1: Must have at least one invoice marked as PAGADA (or PAGADO)
    const hasPaidInvoice = dataRow.facturas?.some(
      (inv) => inv.estadoPago === "PAGADA" || inv.estadoPago === "PAGADO",
    );

    // Condition 2: Difference between OC total and sum of all Invoice Net amounts < 10%
    const sumNetoFacturas = (dataRow.facturas || []).reduce((acc, inv) => {
      const detalles: FacturaDetalle[] = parseJsonArray(
        inv.facturaDetalleArray,
      );
      // 77 Factura exenta has no "Valor Neto" line: see netoFactura
      return acc + netoFactura(detalles, inv.montoFactura, inv.tipoDocumento);
    }, 0);

    const ocTotal = Number(dataRow.montoTotalNetoOrden) || 0;
    const difference = Math.abs(ocTotal - sumNetoFacturas);
    const isDiffLessThan10Percent =
      ocTotal > 0 ? difference / ocTotal <= 0.1 : difference === 0;

    // Button visibility lives in AccionesCumplimiento: "cumplida" needs a
    // paid invoice, the order not already CUMPLIDA (4) and the invoices
    // within 10% of the OC (as stored, or for USD / EUR / UF orders converted
    // at the invoice-date or month-end rate); "semi cumplida" needs an
    // EMITIDA (2) order with a paid invoice when "cumplida" isn't offered

    return (
      <div className="p-3 bg-gray-50 border-round">
        <div className="flex justify-content-between align-items-center mb-3">
          <h5 className="m-0 text-700">
            Detalles de la Orden de Compra #{dataRow.nroOrdenCompra}
          </h5>

          <AccionesCumplimiento
            orden={dataRow}
            sumNetoFacturas={sumNetoFacturas}
            hasPaidInvoice={!!hasPaidInvoice}
            cumpleMontoOc={isDiffLessThan10Percent}
            renderCumplir={() => (
            <Button
              label="MARCAR CUMPLIDA LA ORDEN"
              icon="pi pi-check-circle"
              className="p-button-success p-button-sm shadow-2 font-semibold border-round-3xl"
              style={{ padding: "0.45rem 1.25rem" }}
              onClick={() => confirmMarcarCumplida(dataRow)}
            />
            )}
            renderSemiCumplir={() => (
            <Button
              label="MARCAR SEMI CUMPLIDA"
              icon="pi pi-check-square"
              className="p-button-warning p-button-sm shadow-2 font-semibold border-round-3xl"
              style={{
                padding: "0.45rem 1.25rem",
                backgroundColor: "#f97316",
                borderColor: "#f97316",
                color: "#ffffff",
              }}
              onClick={() => confirmMarcarSemiCumplida(dataRow)}
            />
            )}
          />
        </div>

        <Card title="Facturas Asociadas" className="shadow-1">
          <DataTable
            value={dataRow.facturas}
            size="small"
            dataKey={(invoice: InvoiceItem) =>
              `${invoice.numeroFacturaLegal}-${invoice.numeroFacturaRea}-${invoice.numeroComprobante}`
            }
            expandedRows={expandedInvoiceRows}
            onRowToggle={(e: DataTableRowEvent) =>
              setExpandedInvoiceRows(e.data)
            }
            rowExpansionTemplate={(invoice: InvoiceItem) =>
              invoiceRowExpansionTemplate(invoice, dataRow)
            }
            emptyMessage="Sin facturas asociadas a esta Orden de Compra"
          >
            <Column expander style={{ width: "3rem" }} />

            <Column
              header="Tipo Doc."
              body={(invoice: InvoiceItem) =>
                invoice.tipoDocumento && invoice.nombreDocumento
                  ? `${invoice.tipoDocumento} - ${invoice.nombreDocumento}`
                  : "-"
              }
            />

            <Column
              header="N° Documento / N° Rea"
              body={(invoice: InvoiceItem) =>
                nroFacturaTemplate(invoice, dataRow)
              }
            />
            <Column
              field="identificadorProveedorFactura"
              header="RUT Proveedor"
              body={(inv: InvoiceItem) => {
                const rut =
                  inv.identificadorProveedorFactura ||
                  dataRow.identificadorProveedor ||
                  "-";
                const nombre =
                  (inv as any).nombreProveedor ||
                  (inv as any).razonSocial ||
                  (inv as any).nomProveedor ||
                  "";

                if (rut === "-") return "-";
                return nombre ? `${rut} - ${nombre}` : rut;
              }}
            />
            <Column
              field="facturaGlosa"
              header="Glosa"
              body={(inv: InvoiceItem) => inv.facturaGlosa || "-"}
              style={{ maxWidth: "14rem" }}
            />
            <Column
              header="Monto Neto"
              body={(inv: InvoiceItem) => {
                const detalles: FacturaDetalle[] = parseJsonArray(
                  inv.facturaDetalleArray,
                );
                const netoObj = detalles.find(
                  (det) =>
                    det.FcoTotNom?.trimEnd() === "Valor Neto" ||
                    det.FcoTotNom?.trimEnd() === "Valor Exento",
                );
                // 77 Factura exenta: same amount the cuadratura uses
                if (inv.tipoDocumento === TIPO_FACTURA_EXENTA)
                  return currencyFormatter(
                    netoFactura(detalles, inv.montoFactura, inv.tipoDocumento),
                  );
                return netoObj ? currencyFormatter(netoObj.FcoTotVal) : "-";
              }}
            />
            <Column
              field="fechaFactura"
              header="Fecha Factura"
              body={(inv: InvoiceItem) =>
                inv.fechaFactura
                  ? new Date(inv.fechaFactura).toLocaleDateString("es-CL")
                  : "-"
              }
            />
            <Column
              header="Comprobante / Ref."
              body={(inv: InvoiceItem) => (
                <span>
                  {inv.numeroComprobante || "-"} /{" "}
                  {inv.numeroComprobanteReferencia || "-"}
                </span>
              )}
            />
            <Column
              field="estadoFactura"
              header="Cuadratura Facturas/OC"
              body={estadoFacTemplate}
            />
            <Column
              field="estadoPago"
              header="Estado Pago"
              body={estadoConhisTemplate}
            />
          </DataTable>
        </Card>
      </div>
    );
  };

  return (
    <div className="card" style={{ padding: "50px" }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <DataTable
        value={filteredData}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={loading}
        dataKey="nroOrdenCompra"
        filters={columnFilters}
        filterDisplay="menu"
        onFilter={(e) => setColumnFilters(e.filters as any)}
        header={header}
        emptyMessage="No se encontraron registros."
        expandedRows={expandedRows}
        onRowToggle={(e: DataTableRowEvent) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        stripedRows
        showGridlines
        responsiveLayout="scroll"
      >
        <Column expander style={{ width: "3rem" }} />

        <Column
          field="tipoOrden"
          header="Tipo OC"
          sortable
          filter
          filterElement={tipoOrdenFilterTemplate}
          showFilterMatchModes={false}
          body={(rowData) => {
            switch (rowData.tipoOrden) {
              case 1:
                return "Stock";
              case 2:
                return "Servicios";
              case 3:
                return "Activos/Inversiones";
              default:
                return rowData.tipoOrden || "-";
            }
          }}
        />

        <Column
          field="nroOrdenCompra"
          header="N° Orden Compra"
          sortable
          filter
          filterPlaceholder="Buscar por OC"
          body={nroOrdenCompraTemplate}
          style={{ maxWidth: "4rem" }}
        />

        <Column
          field="identificadorProveedor"
          header="RUT Proveedor OC"
          sortable
          filter
          filterPlaceholder="Buscar por Proveedor"
          body={(rowData: OrdenCompraGroupedResponse) => {
            const rut = rowData.identificadorProveedor?.trim();
            const nombre =
              (rowData as any).nombreProveedor?.trim() ||
              (rowData as any).nomProveedor?.trim() ||
              (rowData as any).razonSocial?.trim() ||
              rowData.facturas?.[0]?.nombreProveedor?.trim() ||
              "";

            if (!rut && !nombre) return "-";
            if (rut && nombre) return `${rut} - ${nombre}`;
            return rut || nombre;
          }}
          style={{ maxWidth: "14rem" }}
        />

        <Column
          field="montoTotalNetoOrden"
          header="Monto Neto OC"
          sortable
          body={(row) => currencyFormatter(row.montoTotalNetoOrden)}
          style={{ maxWidth: "8rem" }}
        />

        <Column
          field="estadoOrden"
          header="Estado OC"
          sortable
          filter
          filterElement={estadoOrdenFilterTemplate}
          showFilterMatchModes={false}
          body={estadoOcTemplate}
        />
      </DataTable>

      {/* SII Modal */}
      <SiiDteStatusModal
        visible={showSiiModal}
        onHide={() => setShowSiiModal(false)}
        invoiceData={selectedInvoiceForSii}
        parentOrden={selectedParentOrdenForSii}
        toast={toast}
      />
    </div>
  );
};

export default OrdenesConsolidadoTable;
